import { useEffect, useState } from "react";
import { Info, ShieldCheck, Question, ArrowSquareOut } from "@phosphor-icons/react";
import api from "../lib/api";

const CONF_BADGE = {
  confirmed: { label: "Confirmed", className: "bg-[#16A34A] text-white" },
  unconfirmed: { label: "Confirm with retailer", className: "bg-[#D97706] text-white" },
  pre_purchase_only: { label: "Pre-purchase only", className: "bg-[#D97706] text-white" },
};

const COMP_LABEL = {
  unconfirmed: "Competitor price-match: ask the retailer",
  pre_purchase_only: "Competitor price-match: pre-purchase only",
  confirmed: "Competitor price-match: yes",
};

/**
 * Inline retailer-policy disclosure. Honest about what we know and don't know
 * so users send polite, accurate requests and retailers don't get spammed.
 */
export default function RetailerPolicyNote({ retailerId, daysLeft }) {
  const [retailer, setRetailer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/retailers");
        if (cancelled) return;
        setRetailer((data || []).find((r) => r.id === retailerId) || null);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [retailerId]);

  if (!retailer) return null;
  const conf = CONF_BADGE[retailer.policy_confidence] || CONF_BADGE.unconfirmed;
  const compLabel = COMP_LABEL[retailer.competitor_match] || COMP_LABEL.unconfirmed;
  return (
    <div className="border border-[#e4e4e7] bg-[#f7f7f8] p-5 mt-4" data-testid="retailer-policy-note">
      <div className="flex items-start gap-3">
        <Info size={20} weight="duotone" color="#002FA7" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="pg-overline">{retailer.name} price guarantee</span>
            <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 uppercase ${conf.className}`} data-testid="retailer-policy-confidence">
              {conf.label}
            </span>
          </div>
          <p className="text-sm mt-2 leading-relaxed text-[#0a0a0a]">{retailer.policy_note}</p>
          <ul className="mt-3 space-y-1.5 text-xs font-mono-pg text-[#52525b]">
            <li className="flex items-center gap-2">
              <ShieldCheck size={12} weight="fill" color="#002FA7" />
              Same-retailer price drop: ~{retailer.policy_days} day window after purchase
              {daysLeft != null && <span className="text-[#0a0a0a]"> · <strong>{daysLeft}d left for this purchase</strong></span>}
            </li>
            <li className="flex items-center gap-2">
              <Question size={12} weight="fill" color="#52525b" />
              {compLabel}
            </li>
          </ul>
          {retailer.policy_url && (
            <a
              href={retailer.policy_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:text-[#00207A] border-b border-[#002FA7] hover:border-[#00207A] pb-0.5"
              data-testid="retailer-policy-link"
            >
              <ArrowSquareOut size={11} weight="bold" /> Read {retailer.name}'s policy
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
